// useApi — fetch wrapper z JWT, auto-401-logout i offline cache fallback (GET).
//
// Cache behavior (Chunk 8 + B3.5.4-hotfix-4 + V3 DN-D):
//   GET /api/weeks                  → TTL 24h (zmienia się raz na tydzień)
//   Klucz cache zawiera ROLĘ (admin/user) — patrz cacheKeyFor.
//   GET /api/tasks                  → TTL 30 min
//   GET /api/photos/:               → TTL 5 min (zmienia się przy upload)
//   GET /api/address/:id/slots      → TTL 30 min (B3.5.4-hotfix-4: offline detail screen)
//   GET /api/config/:id             → TTL 30 min (V3 DN-D, 12.09: bez tego B3 i Tryb lokalny
//                                     nie mają z czego renderować listy slotów; unieważnienie
//                                     po zapisie configu i zmianie typu WE — invalidateCachedPrefix)
//   Inne GETy                       → nie cachowane
//
// V3 słaby zasięg B-1 (B3, DN1 T Martina 12.09):
//   - KAŻDY GET z UI ma AbortController i limit UI_GET_TIMEOUT_MS (6 s). Po limicie:
//     dane z cache (TAKŻE po TTL — lepszy stary ekran niż czarny) + sygnał „słaby zasięg"
//     (baner „Słaby zasięg, pokazuję ostatni stan") + JEDEN cichy retry w tle (30 s),
//     który odświeża cache. Brak cache → ApiError(timeout, offline) z komunikatem PL,
//     nigdy wieszanie.
//   - Non-GET (POST/PUT/DELETE) BEZ domyślnego limitu (AI check potrafi trwać minuty);
//     limit tylko przez opts.timeoutMs.
//   - Tryb lokalny (B9): GET wyłącznie z cache (także po TTL); brak wpisu →
//     ApiError „Brak danych lokalnych dla tego adresu" (noLocalData). Zero sieci.
//   - Licznik GET-ów w locie (netActivity) → kolejka nie startuje uploadu, gdy UI czeka (B4).
//
// Offline flow (navigator.onLine === false):
//   GET offline + cached (także stale) → zwraca cache
//   GET offline + no cache → throw ApiError(offline=true)
//   Inne metody offline → throw ApiError(offline=true) bez cache'a

import { useCallback, useMemo } from 'react';
import { getToken, clearSession, getCurrentSession } from './useAuth.js';
import { getCachedResponseEntry, setCachedResponse } from '../services/offlineDB.js';
import { API_BASE } from '../config.js';
import { t } from '../shared/i18n.js';
import { isLocalMode } from '../services/localMode.js';
import {
  UI_GET_TIMEOUT_MS, SILENT_RETRY_TIMEOUT_MS,
  beginUiGet, endUiGet, markWeakSignal, markSignalOk,
} from '../services/netActivity.js';
// v85 (MONIT 2026-10-05_0423 zad. 2): urządzenie w audycie usunięcia zdjęcia (`?device=`, tylko DELETE zdjęcia).
import { withDeviceParam } from '../services/deviceIdentity.js';

// Stałe PL (kompatybilność testów); ApiError dostaje tekst w bieżącym języku (i18n).
export const NO_LOCAL_DATA_MESSAGE = 'Brak danych lokalnych dla tego adresu. Włącz synchronizację albo otwórz adres przy zasięgu.';
export const WEAK_SIGNAL_MESSAGE = 'Słaby zasięg: serwer nie odpowiedział w 6 s. Spróbuj ponownie przy lepszym zasięgu.';

export class ApiError extends Error {
  constructor(message, { status, body, offline = false, fromCache = false, timeout = false, localMode = false, noLocalData = false } = {}) {
    super(message);
    this.status = status;
    this.body = body;
    this.offline = offline;
    this.fromCache = fromCache;
    this.timeout = timeout;
    this.localMode = localMode;
    this.noLocalData = noLocalData;
  }
}

export function cacheTTL(path) {
  if (path.startsWith('/api/weeks')) return 24 * 60 * 60 * 1000;
  if (path.startsWith('/api/tasks')) return 30 * 60 * 1000;
  if (path.startsWith('/api/ak/tasks')) return 30 * 60 * 1000; // AK E1: lista AK dnia także bez zasięgu
  if (path.startsWith('/api/photos/')) return 5 * 60 * 1000;
  // B3.5.4-hotfix-4 (audit finding D2): offline detail screen — technicy
  // w terenie wchodzą w adres bez sieci po pierwszym fetch online. Cache
  // 30 min wystarcza dla typowej sesji terenowej, dane refetched po online.
  if (path.startsWith('/api/address/') && path.endsWith('/slots')) return 30 * 60 * 1000;
  // V3 DN-D (T Martina 12.09): config adresu do cache — offline/timeout/Tryb lokalny
  // renderują właściwą listę slotów zamiast fallbacku „T1" (który ZNIKA).
  if (path.startsWith('/api/config/')) return 30 * 60 * 1000;
  // v82 (MONIT 2026-10-04_1357 Zasada 2.9): plan posesji do pamięci offline (plan spawów bez sieci, opis sekcji).
  if (path.startsWith('/api/address/') && path.endsWith('/plan')) return 30 * 60 * 1000;
  return 0; // no cache
}

/**
 * Klucz cache = rola + ścieżka.
 *
 * Od 24.08.2026 `/api/weeks` zwraca RÓŻNE listy dla admina (52 tygodnie wstecz)
 * i technika (12), więc wspólny klucz oznaczałby, że kto pierwszy wejdzie, ten
 * narzuca listę drugiemu na całą dobę (TTL 24h). Rolę wpinamy do klucza dla
 * WSZYSTKICH cache'owanych GET-ów, nie tylko /api/weeks: `/api/tasks` też jest
 * filtrowane po zespole, więc dzielenie wpisu między sesjami było i tak ryzykowne.
 *
 * 15.09.2026: viewer-admin (mały admin) dostaje z `/api/weeks` WŁASNĄ listę
 * (limits.viewer_admin_weeks_back), więc ma osobny segment klucza `viewer:` —
 * inaczej sesja Martina i Bernarda na jednym telefonie dzieliłyby wpis offline.
 * Klucze `admin:` / `user:` bez zmian (stare wpisy cache nadal trafiają).
 *
 * @param {string} path
 * @returns {string}
 */
export function cacheKeyFor(path) {
  const team = getCurrentSession()?.team;
  const scope = team?.role === 'viewer-admin' ? 'viewer' : (team?.isAdmin ? 'admin' : 'user');
  return `${scope}:${path}`;
}

// Cichy retry w tle po timeoucie (jeden na ścieżkę naraz; odświeża cache i gasi sygnał).
const silentRetries = new Map();
function scheduleSilentRetry(path, cacheKey, ttl, headers) {
  if (silentRetries.has(cacheKey)) return;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), SILENT_RETRY_TIMEOUT_MS);
  const p = (async () => {
    try {
      const res = await fetch(`${API_BASE}${path}`, { method: 'GET', headers, signal: controller.signal });
      if (!res.ok) return;
      const ct = res.headers.get('content-type') || '';
      const payload = ct.includes('application/json') ? await res.json().catch(() => null) : await res.text();
      if (payload == null) return;
      await setCachedResponse(cacheKey, payload, ttl).catch(() => {});
      markSignalOk(path);
    } catch {
      /* nadal słabo — baner zostaje do WEAK_SIGNAL_TTL_MS albo następnego sukcesu */
    } finally {
      clearTimeout(timer);
      silentRetries.delete(cacheKey);
    }
  })();
  silentRetries.set(cacheKey, p);
}

/**
 * @param {string} method
 * @param {string} path
 * @param {any} body
 * @param {{ signal?: AbortSignal, skipAuth?: boolean, isFormData?: boolean,
 *           timeoutMs?: number|null }} [opts]
 */
export async function apiFetch(method, path, body, { signal, skipAuth = false, isFormData = false, timeoutMs } = {}) {
  const isGet = method === 'GET';
  const ttl = cacheTTL(path);
  const isCacheable = isGet && ttl > 0;

  const cacheKey = isCacheable ? cacheKeyFor(path) : null;

  // V3 B9 Tryb lokalny: GET wyłącznie z cache (także po TTL). Zero sieci.
  if (isGet && isLocalMode()) {
    if (isCacheable) {
      const entry = await getCachedResponseEntry(cacheKey).catch(() => null);
      if (entry) return entry.data;
    }
    throw new ApiError(t('api.noLocalData'), { offline: true, localMode: true, noLocalData: true });
  }

  // Offline preflight — dla cacheable GET spróbuj cache (także stale) zanim uderzymy w network
  if (isCacheable && typeof navigator !== 'undefined' && !navigator.onLine) {
    const entry = await getCachedResponseEntry(cacheKey).catch(() => null);
    if (entry) return entry.data;
    throw new ApiError(t('api.offlineNoCache'), { offline: true, noLocalData: true });
  }

  const headers = {};
  if (!skipAuth) {
    const token = getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }
  if (body && !isFormData) headers['Content-Type'] = 'application/json';

  // V3 B3: limit czasu. GET z UI domyślnie 6 s; inne metody tylko na życzenie.
  const effTimeout = timeoutMs !== undefined ? timeoutMs : (isGet ? UI_GET_TIMEOUT_MS : null);
  const controller = effTimeout ? new AbortController() : null;
  let timedOut = false;
  let timer = null;
  if (controller) {
    timer = setTimeout(() => { timedOut = true; controller.abort(); }, effTimeout);
    if (signal) {
      if (signal.aborted) controller.abort();
      else signal.addEventListener('abort', () => controller.abort(), { once: true });
    }
  }

  let res;
  if (isGet) beginUiGet();
  try {
    res = await fetch(`${API_BASE}${withDeviceParam(method, path)}`, {
      method,
      headers,
      body: isFormData ? body : body ? JSON.stringify(body) : undefined,
      signal: controller ? controller.signal : signal,
    });
  } catch (err) {
    // Network error (offline mid-request, DNS fail) ALBO nasz timeout (słaby zasięg)
    if (isCacheable) {
      const entry = await getCachedResponseEntry(cacheKey).catch(() => null);
      if (entry) {
        if (timedOut) {
          markWeakSignal(path);
          scheduleSilentRetry(path, cacheKey, ttl, headers);
        }
        return entry.data;
      }
    }
    if (timedOut) {
      markWeakSignal(path);
      throw new ApiError(t('api.weakSignal'), { offline: true, timeout: true, noLocalData: isCacheable });
    }
    throw new ApiError(`Network error: ${err.message}`, { offline: true });
  } finally {
    if (timer) clearTimeout(timer);
    if (isGet) endUiGet();
  }

  if (res.status === 401 && !skipAuth) {
    clearSession();
  }

  const contentType = res.headers.get('content-type') || '';
  const isJson = contentType.includes('application/json');
  const payload = isJson ? await res.json().catch(() => null) : await res.text();

  if (!res.ok) {
    // Dla cacheable GET — jeśli 5xx, spróbuj cache jako fallback
    if (isCacheable && res.status >= 500) {
      const entry = await getCachedResponseEntry(cacheKey).catch(() => null);
      if (entry) return entry.data;
    }
    throw new ApiError(
      payload?.error || payload?.message || `HTTP ${res.status}`,
      { status: res.status, body: payload }
    );
  }

  // Sukces + cacheable → zapisz cache (fire-and-forget); udany GET gasi sygnał słabego zasięgu
  if (isCacheable) {
    setCachedResponse(cacheKey, payload, ttl).catch(() => {});
  }
  if (isGet) markSignalOk(path);
  return payload;
}

export function useApi() {
  const get    = useCallback((path, opts) => apiFetch('GET', path, null, opts), []);
  const post   = useCallback((path, body, opts) => apiFetch('POST', path, body, opts), []);
  const patch  = useCallback((path, body, opts) => apiFetch('PATCH', path, body, opts), []);
  // PAKIET 6 (30.08): PUT dla /api/admin/settings/* (Tryb Serwisowy Pełny).
  const put    = useCallback((path, body, opts) => apiFetch('PUT', path, body, opts), []);
  // B3.5.9: del akceptuje opcjonalne body (analogicznie do post/patch). Backward
  // compat: del(path) bez body działa jak poprzednio. Użycie: del(path, {reason: '...'}).
  const del    = useCallback((path, body, opts) => apiFetch('DELETE', path, body || null, opts), []);
  const upload = useCallback(async (path, formData, opts) =>
    apiFetch('POST', path, formData, { ...opts, isFormData: true }), []);
  return useMemo(() => ({ get, post, patch, put, del, upload }), [get, post, patch, put, del, upload]);
}
